'use client';

import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ArrowDownOutlined } from '@/shared/components/icons';

type Props = {
    children: ReactNode;
};

export function ListingCollapse({ children }: Props) {
    const contentRef = useRef<HTMLDivElement>(null);

    const initialHeight = 300;
    const overlayHeight = 150;

    const [open, setOpen] = useState(false);
    const [isCollapsible, setIsCollapsible] = useState(false);
    const [height, setHeight] = useState<number | 'auto'>('auto');

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const contentHeight = el.scrollHeight;
        const shouldCollapse = contentHeight > initialHeight;

        setIsCollapsible(shouldCollapse);
        setHeight(shouldCollapse ? initialHeight : 'auto');
    }, [children]);

    useEffect(() => {
        const el = contentRef.current;
        if (!el || !isCollapsible) return;

        setHeight(open ? el.scrollHeight : initialHeight);
    }, [open, isCollapsible]);

    return (
        <div
            className="relative transition-[height] duration-300 ease-out"
            style={{
                height: height === 'auto' ? undefined : height,
            }}
        >
            <div
                ref={contentRef}
                className="overflow-hidden transition-[height] duration-300 ease-out"
                style={{
                    height: height === 'auto' ? undefined : `${height}px`,
                }}
            >
                {children}
            </div>

            {!open && isCollapsible && (
                <div
                    className="absolute bottom-0 z-[1] w-full bg-gradient-to-t from-white to-transparent"
                    style={{ height: overlayHeight }}
                />
            )}

            {isCollapsible && (
                <div className="absolute bottom-[-30px] z-[2] w-full">
                    <div className="relative flex w-full items-center justify-center">
                        <button
                            type="button"
                            className="flex items-center gap-[7px] cursor-pointer"
                            onClick={() => setOpen((value) => !value)}
                        >
                            <ArrowDownOutlined
                                className={`py-[3px] w-[17px] transition-transform duration-300 text-cust-primary ${
                                    open ? 'rotate-180' : ''
                                }`}
                            />
                            <span className="font-[600] text-[14px]">
                                {open ? 'Zbaliť' : 'Celý popis'}
                            </span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
